import * as React from "react";

import ReactDOM from "react-dom";
import { Router, browserHistory } from "react-router";

import { LoadingPage } from "@/components/LoadingPage";
import { initAuth } from "@/services/auth.service";

import { ErrorBoundary } from "./ErrorBoundary";
import { IntlProvider } from "./i18n/IntlProvider";
import { initializeDatadog } from "./lib/datadog/datadog.utils";
import { startMockServiceWorker } from "./mocks/start";
import routes from "./routes";
import { initWebEnv } from "./services/env.service";
import "./styles.css";
import { InitialAppData } from "./types/app.types";

/**
 * Reads and parses raw data from a script tag. This is a safe way to pass data from server to
 * client. Returns fallback if the tag or its content is missing or invalid.
 */
const getJSONFromScript = (id: string, fallback: any = {}) => {
  try {
    const scriptEl = document.getElementById(id);
    if (!scriptEl) {
      return fallback;
    }
    return JSON.parse(scriptEl.textContent || "{}");
  } catch {
    return fallback;
  }
};

// Load initial data from the server
const { locale, localeDataMap, booking, isBot } = getJSONFromScript(
  "__INITIAL_DATA__",
  {
    locale: "en-US",
    localeDataMap: {},
    isBot: false,
  } as InitialAppData,
);
window.isBot = isBot;

// Load public env variables from the server and set on the window
const publicEnv = getJSONFromScript("__PUBLIC_ENV__", {});
window.PUBLIC_ENV = publicEnv;

// Start the app once optional dev request mocking is ready, so MSW can
// intercept the init fetches below. In production this resolves immediately.
startMockServiceWorker().finally(() => {
  // @TODO: what do we do if these fail?
  initAuth();
  initWebEnv();

  initializeDatadog();

  ReactDOM.render(
    <React.StrictMode>
      <React.Suspense fallback={<LoadingPage className="tw-h-screen" />}>
        <IntlProvider locale={locale} localeDataMap={localeDataMap}>
          <ErrorBoundary>
            <Router
              history={browserHistory}
              routes={routes}
              createElement={(Component, props) => (
                <Component {...props} booking={booking} />
              )}
            />
          </ErrorBoundary>
        </IntlProvider>
      </React.Suspense>
    </React.StrictMode>,
    document.getElementById("root"),
  );
});
